import { Check, Clock4, MessageSquareMore, Tag } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedText, Loader, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { BookingAppointmentTypeOnboarding } from '../../components/booking-onboarding/booking-appointment-type-onboarding'
import { BookingAvailabilityOnboarding } from '../../components/booking-onboarding/booking-availability-onboarding'
import { BookingReminderSettingsOnboarding } from '../../components/booking-onboarding/booking-reminder-settings-onboarding'
import { OnboardingHeader } from '../../components/onboarding-header'
import { SyncOfflineChangesModal } from '../../components/send-test-or-finish/sync-offline-changes-modal'
import classes from './booking-onboarding-wrapper.module.scss'

interface ProgressStep {
  step: number
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  title: string
  alignment: 'left' | 'center' | 'right'
}

const progressSteps: ProgressStep[] = [
  { step: 1, icon: Clock4, title: 'Availability', alignment: 'left' },
  { step: 2, icon: Tag, title: 'Appointment Type', alignment: 'center' },
  { step: 3, icon: MessageSquareMore, title: 'SMS Reminders', alignment: 'right' },
]

export function BookingOnboardingWrapper() {
  const profile = useProfile()
  const bookingOnboarding = useBookingOnboarding()
  const navigate = useNavigate()

  useEffect(() => {
    if (profile.isLoading || bookingOnboarding.isLoading) {
      return
    }
    if (profile.data && !profile.data.is_booking_user) {
      navigate(routeNames.configuration.path, { replace: true })
      return
    }
    if (bookingOnboarding.data?.onboarding_completed && profile.data?.refresh_token) {
      navigate(`${routeNames.settings.path}?tab=booking-page`, { replace: true })
    }
  }, [
    bookingOnboarding.data?.onboarding_completed,
    bookingOnboarding.isLoading,
    navigate,
    profile.data,
    profile.isLoading,
  ])

  if (profile.isLoading || bookingOnboarding.isLoading) {
    return <Loader isLoading text={<FormattedText id="configuration.profileLoadingText" />} full />
  }

  // Redirects above run in an effect; avoid rendering steps until profile shape matches this flow
  if (profile.data && !profile.data.is_booking_user) {
    return null
  }

  if (bookingOnboarding.data?.onboarding_completed && profile.data?.refresh_token) {
    return null
  }

  const onboardingData = bookingOnboarding.data

  // Current step
  let currentStep = 1
  if (onboardingData?.availability_set) {
    currentStep = 2
  }
  if (onboardingData?.availability_set && onboardingData?.appointment_type_created) {
    currentStep = 3
  }

  // Step 1: Availability not set
  if (!onboardingData?.availability_set) {
    return (
      <>
        <OnboardingHeader classes={classes} />
        <ProgressIndicator currentStep={currentStep} />
        <div className={classes.content}>
          <BookingAvailabilityOnboarding />
        </div>
      </>
    )
  }

  // Step 2: Appointment type not created
  if (!onboardingData?.appointment_type_created) {
    return (
      <>
        <OnboardingHeader classes={classes} />
        <ProgressIndicator currentStep={currentStep} />
        <div className={classes.content}>
          <BookingAppointmentTypeOnboarding />
        </div>
      </>
    )
  }

  // Step 3: Reminder settings not set
  if (!onboardingData?.reminder_settings_set) {
    return (
      <>
        <OnboardingHeader classes={classes} />
        <ProgressIndicator currentStep={currentStep} />
        <div className={classes.content}>
          <BookingReminderSettingsOnboarding />
        </div>
      </>
    )
  }

  // Step 4: All steps complete, show sync offline changes (no header)
  return <SyncOfflineChangesModal isBookingUser={true} />
}

function ProgressIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className={classes.progressIndicator}>
      <div className={classes.progressLine}>
        {progressSteps.map((stepInfo, index) => {
          const step = stepInfo.step
          const isComplete = step < currentStep
          const isCurrent = step === currentStep
          const IconComponent = stepInfo.icon
          const showConnector = index < progressSteps.length - 1
          const isConnectorComplete = showConnector && step < currentStep

          return (
            <div key={step} className={classes.progressStep}>
              <div
                className={`${classes.progressStepContent} ${
                  stepInfo.alignment === 'left'
                    ? classes.progressStepContentLeft
                    : stepInfo.alignment === 'center'
                      ? classes.progressStepContentCenter
                      : classes.progressStepContentRight
                }`}
              >
                <div
                  className={`${classes.progressCircle} ${isCurrent ? classes.progressCircleCurrent : ''} ${
                    isComplete ? classes.progressCircleComplete : ''
                  }`}
                >
                  {isComplete ? (
                    <Check size={20} className={classes.checkmark} />
                  ) : isCurrent ? (
                    <div className={classes.progressCircleFilled} />
                  ) : null}
                </div>
              </div>
              {showConnector && (
                <div
                  className={`${classes.progressConnector} ${
                    isConnectorComplete ? classes.progressConnectorComplete : ''
                  } ${
                    stepInfo.alignment === 'left'
                      ? classes.progressConnectorFromLeft
                      : stepInfo.alignment === 'center'
                        ? classes.progressConnectorFromCenter
                        : classes.progressConnectorFromRight
                  }`}
                />
              )}
              <div
                className={`${classes.progressStepLabel} ${
                  stepInfo.alignment === 'left'
                    ? classes.progressStepLabelLeft
                    : stepInfo.alignment === 'center'
                      ? classes.progressStepLabelCenter
                      : classes.progressStepLabelRight
                }`}
              >
                <IconComponent size={16} className={classes.progressIcon} />
                <Typography variant="body" className={classes.progressTitle}>
                  {stepInfo.title}
                </Typography>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
